import { AnalyticsLogResponse } from '@kcftech/prometheustypescriptapiclient';
import { DefaultError, InfiniteData, useInfiniteQuery } from '@tanstack/react-query';
import { sdClient } from 'contexts/SDKClientContext';
import { addMilliseconds, subMilliseconds } from 'date-fns';
import { useEffect, useMemo } from 'react';

const pollingIntervalMs = 30 * 1000;
// Page size small due to the 1mb limit for now
const pageSize = 10;
export const modelLogQueryKey = 'model-logs';

type InternalLogResults = {
    logs: AnalyticsLogResponse[];
    startDate: Date;
    endDate: Date;
    page: number;
    hasMoreForRange?: boolean;
};

export type UseAnalyticsModelLogs = Readonly<{
    /**
     * Model Id
     */
    modelId: string;
    /**
     * Indicators polling
     */
    pollingEnabled: boolean;

    /**
     * Time range to start scrolling at
     */
    initialTimeRange: number;
    disableEndDate?: Date;
}>;

export function usePollingAnalyticsModelLogs({
    modelId,
    pollingEnabled,
    initialTimeRange,
    disableEndDate,
}: UseAnalyticsModelLogs) {
    const { isLoading, fetchNextPage, data, isFetching } = useInfiniteQuery<
        InternalLogResults,
        DefaultError,
        InfiniteData<InternalLogResults>,
        readonly unknown[],
        Pick<InternalLogResults, 'startDate' | 'endDate' | 'page' | 'hasMoreForRange'>
    >({
        queryKey: ['analytics-model-logs', modelId, initialTimeRange],
        queryFn: async ({ pageParam }) => {
            const endDate = disableEndDate ?? pageParam.endDate;
            const startDate = pageParam.startDate;
            const page = pageParam.page;
            const { Result, TotalPages } = await sdClient.getAnalyticsLogs({
                modelId,
                startDate,
                endDate,
                page,
                pageLimit: pageSize,
            });

            const hasMoreForRange = page < TotalPages;
            return {
                logs: Result,
                startDate,
                endDate,
                page,
                hasMoreForRange,
            };
        },

        refetchInterval: pollingEnabled ? pollingIntervalMs : undefined,
        initialPageParam: {
            startDate: subMilliseconds(disableEndDate ?? new Date(), initialTimeRange),
            endDate: disableEndDate ?? new Date(),
            page: 0,
        },
        getNextPageParam: ({ hasMoreForRange, startDate, endDate, page }) =>
            // If there are more results for this date range,
            // use the same dates and increment the page number.
            // Otherwise, shift the dates and reset the page number.
            hasMoreForRange
                ? { startDate, endDate, page: page + 1 }
                : {
                      startDate: addMilliseconds(endDate, 1),
                      endDate: new Date(),
                      page: 1,
                  },
    });
    // Check if there are more pages available for this date range and
    // auto fetch until there are no more results for the time range
    useEffect(() => {
        if (
            !isLoading &&
            data &&
            data.pages.length > 0 &&
            data.pages[data.pages.length - 1].hasMoreForRange
        ) {
            fetchNextPage();
        }
    }, [data, fetchNextPage, isLoading]);

    const logs = useMemo(() => data?.pages.flatMap((page) => page.logs) ?? [], [data]);
    return { isLoading, logs, isFetching };
}
